import {
  type CommonPowerSyncDatabase,
  type CrudEntry,
  type PowerSyncBackendConnector,
  type PowerSyncCredentials,
  UpdateType,
} from '@powersync/common';
import { GetTokenOptions } from '../../providers/types';
import { ApiClient } from '../laravel/ApiClient';
import { parseJsonColumns } from '../laravel/jsonColumns';
import { isIdKeyedPivot } from '../laravel/utils';
import { describeError, powersyncLogger } from '../logger';

/** 4xx responses the backend can still accept on a later attempt. */
const RETRYABLE_STATUS_CODES = new Set([
  401, // token expired or rotated — the next attempt resolves a fresh one
  408, // request timeout
  419, // Laravel's "authentication timeout"
  429, // rate limited
]);

/**
 * Whether a rejected write is worth sending again.
 *
 * This replaces a Postgres SQLSTATE check that could never match. Uploads go through the Laravel
 * REST API via axios, so the thrown error carries an axios code (`ERR_BAD_REQUEST`, `ERR_NETWORK`)
 * and never a SQLSTATE like `23505` — which meant every permanent rejection (422 validation, 403
 * policy, 404 missing row) was classified as retryable. Because uploads always resume from the
 * oldest queued transaction, a single rejected write then blocked every write behind it forever.
 *
 * No response at all means the request never reached the server (offline, DNS, timeout) — always
 * retry. 5xx is the server asking to be tried later. The rest of the 4xx range is the server
 * saying this exact payload will never be accepted, so the write is dropped; PowerSync reverts the
 * row to server state on the next checkpoint.
 *
 * A thrown value with no status at all is treated as retryable on purpose: that is either a
 * transport failure or a bug on our side, and neither is a reason to silently drop a user's write.
 */
const isRetryableUploadStatus = (status: unknown): boolean => {
  if (typeof status !== 'number') {
    return true;
  }

  return status >= 500 || RETRYABLE_STATUS_CODES.has(status);
};

/**
 * How many times one op waits out a 429 before giving up and abandoning the transaction. Each wait
 * is the server's own `Retry-After`, at most a minute, so this covers a few minutes of throttling.
 */
const MAX_THROTTLE_WAITS = 5;

/** Laravel throttles per minute; used when a 429 carries no readable `Retry-After`. */
const DEFAULT_THROTTLE_WAIT_MS = 60_000;

/**
 * How long the server asked us to wait, or `null` when the error is not a 429.
 *
 * Read structurally, like `describeError`, rather than through axios: the header is on
 * `response.headers`, which axios lowercases.
 */
const throttleWaitMs = (error: unknown): number | null => {
  if (describeError(error).status !== 429) {
    return null;
  }

  const headers = (error as { response?: { headers?: Record<string, unknown> } }).response?.headers;
  const seconds = Number(headers?.['retry-after']);

  return Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : DEFAULT_THROTTLE_WAIT_MS;
};

const TOKEN_TEMPLATE = 'one_hour_token';

/**
 * What `getAuthToken` throws when Clerk has no session to issue a token for — `getToken` resolves
 * `null` for exactly that and nothing else. It is the state of every signed-out page, and of the
 * moment between a sign-out and the wipe that disconnects sync, so it is not a sync failure.
 */
const NO_SESSION_MESSAGE = `No Clerk session to issue a "${TOKEN_TEMPLATE}" token for`;

/**
 * Whether an error is `getAuthToken` finding no Clerk session.
 *
 * Matched on the message because on web the error reaches `statusChanged` from the sync worker,
 * which posts it across as a plain `{ name, message, stack }` — neither the class nor a `code` survive.
 */
export const isNoClerkSessionError = (error: unknown): boolean =>
  typeof error === 'object' && error !== null && (error as { message?: unknown }).message === NO_SESSION_MESSAGE;

type Config = {
  powersyncInstanceUrl?: string;
  laravelBeHostUrl?: string;
  getToken: (options?: GetTokenOptions) => Promise<string | null>;
};

export class ClerkConnector implements PowerSyncBackendConnector {
  readonly config: Config;
  private apiClient?: ApiClient;
  private initPromise: Promise<void> | null = null;

  ready: boolean;

  constructor(config: Config) {
    this.config = config;
    this.ready = false;
  }

  /**
   * Idempotent — concurrent and repeated calls share a single initialization, and every
   * caller resolves once the connector is ready. A failed attempt is discarded so a later
   * call retries instead of resolving against a connector that never initialized.
   */
  init() {
    this.initPromise ??= this.performInit().catch((err) => {
      if (isNoClerkSessionError(err)) {
        powersyncLogger.warn('connector init skipped, no Clerk session');
      } else {
        powersyncLogger.exception('connector init failed', err);
      }
      this.initPromise = null;
      throw err;
    });

    return this.initPromise;
  }

  private async performInit() {
    if (!this.config.laravelBeHostUrl) {
      throw new Error('Failed to initialize ClerkConnector: No Laravel BE Host URL provided');
    }

    // A reachability probe, nothing more: it proves a token is obtainable and surfaces an auth
    // problem early. The token is not kept, and uploads do not depend on this having run —
    // see `getApiClient`.
    await this.getAuthToken();

    this.ready = true;
  }

  /**
   * Built on first use rather than during `init`. Nothing about the client is async any more (the
   * token is resolved per request), so making uploads wait on a successful `init` would only mean
   * an app that launched without a network can never upload, even after it comes back online.
   */
  private getApiClient(): ApiClient {
    if (!this.config.laravelBeHostUrl) {
      throw new Error('Failed to upload: No Laravel BE Host URL provided');
    }

    this.apiClient ??= new ApiClient({
      getAuthToken: () => this.getAuthToken(),
      laravelBeHostUrl: this.config.laravelBeHostUrl,
    });

    return this.apiClient;
  }

  /**
   * The one place a Clerk token is read. Nothing caches the result: Clerk already returns the
   * current token until it nears expiry, so this is cheap, and it means no code path can end up
   * presenting the token that happened to be valid when the connector was constructed.
   */
  private async getAuthToken(): Promise<string> {
    const token = await this.config.getToken({ template: TOKEN_TEMPLATE });

    if (!token) {
      throw new Error(NO_SESSION_MESSAGE);
    }

    return token;
  }

  async fetchCredentials() {
    if (!this.config.powersyncInstanceUrl) {
      throw new Error('Failed to fetch credentials: No PowerSync Instance URL provided');
    }

    const token = await this.getAuthToken();

    // Fires on connect and on every re-auth, so the cadence here is how you tell a healthy
    // long-lived session from one that is silently failing to refresh.
    powersyncLogger.info('credentials issued', { endpoint: this.config.powersyncInstanceUrl });

    return {
      endpoint: this.config.powersyncInstanceUrl,
      token,
    } satisfies PowerSyncCredentials;
  }

  async uploadData(database: CommonPowerSyncDatabase): Promise<void> {
    const transaction = await database.getNextCrudTransaction();
    if (!transaction) {
      return;
    }

    const startedAt = Date.now();
    const ops = transaction.crud.length;
    let discarded = 0;

    // Note: If transactional consistency is important, use database functions
    // or edge functions to process the entire transaction in a single call.
    for (const [index, op] of transaction.crud.entries()) {
      try {
        await this.applyOpWaitingOutThrottle(op, transaction.transactionId);
      } catch (ex) {
        const retryable = isRetryableUploadStatus(describeError(ex).status);

        powersyncLogger.exception('upload op failed', ex, {
          transactionId: transaction.transactionId,
          table: op.table,
          op: op.op,
          opIndex: index,
          ops,
          retryable,
          durationMs: Date.now() - startedAt,
        });

        if (retryable) {
          // Abandon the rest of the transaction. PowerSync can only acknowledge a transaction as a
          // unit, so the ops already sent will be re-sent on the next attempt — safe because every
          // upload is idempotent: PUT/PATCH key on the row's uuid, and a repeated DELETE 404s,
          // which lands on the permanent branch below.
          throw ex;
        }

        // Permanently rejected: drop this op alone and keep going, so the valid writes queued
        // behind it in the same transaction still reach the server. The dropped row reverts to
        // server state on the next checkpoint.
        discarded += 1;
      }
    }

    await transaction.complete();

    if (discarded > 0) {
      powersyncLogger.warn('upload complete with discarded ops', {
        transactionId: transaction.transactionId,
        ops,
        discarded,
        durationMs: Date.now() - startedAt,
      });
      return;
    }

    powersyncLogger.info('upload complete', {
      transactionId: transaction.transactionId,
      ops,
      durationMs: Date.now() - startedAt,
    });
  }

  /**
   * Sends one op, waiting out the backend's per-user write throttle (MDC-4644) and resending the
   * same op, instead of throwing straight away. Throwing abandons the transaction, and since
   * PowerSync acknowledges a transaction only as a unit, the next attempt replays it from op 0:
   * one with more ops than a minute's budget would spend every attempt re-sending the ops that
   * already landed and be throttled at the same index again, blocking the queue for good.
   *
   * Once the waits run out the 429 is rethrown, and `uploadData` keeps the transaction queued.
   */
  private async applyOpWaitingOutThrottle(op: CrudEntry, transactionId: number | undefined): Promise<void> {
    for (let waits = 0; ; waits += 1) {
      try {
        await this.applyOp(op);
        return;
      } catch (ex) {
        const waitMs = throttleWaitMs(ex);
        if (waitMs === null || waits >= MAX_THROTTLE_WAITS) {
          throw ex;
        }

        powersyncLogger.warn('upload throttled, waiting', { transactionId, table: op.table, waitMs, waits });
        await new Promise((resolve) => setTimeout(resolve, waitMs));
      }
    }
  }

  private async applyOp(op: CrudEntry): Promise<void> {
    const apiClient = this.getApiClient();
    // The row as written locally, minus the one difference the API insists on: a column SQLite
    // could only hold as JSON text is sent as the document it is.
    const record = parseJsonColumns(op.table, { ...op.opData, ...this.getIdColumn(op) });

    switch (op.op) {
      case UpdateType.PUT:
        await apiClient.upsert(record, op.table);
        break;
      case UpdateType.PATCH:
        await apiClient.update(record, op.table);
        break;
      case UpdateType.DELETE:
        await apiClient.delete(record, op.table);
        break;
      default:
        // Unreachable for today's UpdateType. Guards the case this switch is silent about: an op
        // that matches nothing returns normally, which reads as "uploaded" and gets acknowledged
        // by `transaction.complete()` — the write would be dropped without ever being sent.
        throw new Error(`Unsupported CRUD op "${op.op}" for table "${op.table}"`);
    }
  }
  private getIdColumn(op: CrudEntry) {
    if (isIdKeyedPivot(op.table)) {
      return {
        id: op.id,
      };
    }
    return {
      uuid: op.id,
    };
  }
}
